import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Users as UsersIcon, Plus, Save, X, Mail, CheckCircle2, CircleSlash2, ArrowLeft, Search, Filter, Download, ChevronLeft, ChevronRight, SortAsc, SortDesc, Thermometer, Droplets, Waves } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import userService from '../services/userService';
import permissionService from '../services/permissionService';
import wqmsService from '../services/wqmsService';
import ActivityLogs from './ActivityLogs';
const roleLabel = {
  admin: 'Super Admin',
  editor: 'Admin',
  viewer: 'Viewer',
};

const getRoleLabel = (role) => roleLabel[String(role || '').toLowerCase()] || role;
const EXPORT_BUTTONS_VISIBLE = false;
const UsersDashboard = () => {
  const { user, hasAccess, isAdmin } = useAuth();
  const currentRole = String(user?.role || '').toLowerCase();
  const isSuperAdmin = isAdmin || currentRole === 'admin';
  const isLimitedManager = !isSuperAdmin && hasAccess('users:dashboard');
  const canCreateUsers = isSuperAdmin || hasAccess('users:create');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formMode, setFormMode] = useState('create'); // 'create' | 'edit'
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ id: null, firstName: '', lastName: '', email: '', password: '', isActive: true, role: 'viewer' });
  const [showModal, setShowModal] = useState(false);

  // UI/UX enhancements
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'active' | 'inactive'
  const [sortKey, setSortKey] = useState('createdAt'); // 'firstName' | 'email' | 'createdAt' | 'isActive'
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'
  const [toasts, setToasts] = useState([]); // { id, type: 'success'|'error', message }
  const [showActivityLogs, setShowActivityLogs] = useState(false);
  const [stations, setStations] = useState([]);
  const [stationsLoading, setStationsLoading] = useState(true);
  const [stationsError, setStationsError] = useState('');
  const canViewActivityLogs = isSuperAdmin || currentRole === 'editor';
  const canAccessUsers = isSuperAdmin || hasAccess('users:dashboard');
  const isOwnedByMe = useCallback(
    (u) => Number(u?.createdBy) === Number(user?.id),
    [user?.id]
  );
  const filterVisibleUsers = useCallback(
    (list) => {
      if (!isLimitedManager) return list;
      return (list || []).filter(
        (u) => String(u.role || '').toLowerCase() === 'viewer' && isOwnedByMe(u)
      );
    },
    [isLimitedManager, isOwnedByMe]
  );

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await userService.list();
      if (r.success) {
        setUsers(filterVisibleUsers(r.data.users || []));
      }
      else setError('Unable to load users');
    } catch (e) {
      setError('Network error while loading users.');
    } finally {
      setLoading(false);
    }
 }, [filterVisibleUsers]);

  useEffect(() => {
    if (canAccessUsers) loadUsers();
  }, [loadUsers, canAccessUsers]);

  const startCreate = () => {
    setFormMode('create');
    setFormData({ id: null, firstName: '', lastName: '', email: '', password: '', isActive: true, role: 'viewer' });
    setShowModal(true);
  };

  const startEdit = async (u) => {
      if (isLimitedManager && (String(u.role || '').toLowerCase() !== 'viewer' || !isOwnedByMe(u))) {
      setToasts((t) => [...t, { id: Date.now(), type: 'error', message: 'You can edit only users you created' }]);
      return;
    }
    try {
      // Refresh users to ensure we have the latest role after permission changes elsewhere
      const r = await userService.list();
      if (r.success) {
        setUsers(filterVisibleUsers(r.data.users || []));
        const latest = filterVisibleUsers(r.data.users || []).find((x) => x.id === u.id) || u;
        setFormMode('edit');
        setFormData({ id: latest.id, firstName: latest.firstName, lastName: latest.lastName, email: latest.email, password: '', isActive: latest.isActive, role: latest.role || 'viewer' });
        setShowModal(true);
        return;
      }
    } catch (e) {
      // fallthrough to open with provided data
    }
    setFormMode('edit');
    setFormData({ id: u.id, firstName: u.firstName, lastName: u.lastName, email: u.email, password: '', isActive: u.isActive, role: u.role || 'viewer' });
    setShowModal(true);
  };

  const submit = async () => {
    try {
      setSaving(true);
      if (formMode === 'create') {
        const safeRole = isSuperAdmin ? formData.role : 'viewer';
const payload = { firstName: formData.firstName, lastName: formData.lastName, email: formData.email, password: formData.password, role: safeRole };
        const r = await userService.create(payload);
        if (!r.success) throw new Error(r.message || 'Failed to create');
        // Sync dashboard permission to role for new user
        try {
          const newUserId = r.data?.user?.id;
          if (newUserId) {
            await permissionService.save({ subjectType: 'user', subjectId: newUserId, permission: safeRole, resource: 'wqms_dashboard' });
          }
        } catch (_) { /* non-blocking */ }
        setToasts((t) => [...t, { id: Date.now(), type: 'success', message: 'User created' }]);
      } else {
        const safeRole = isSuperAdmin ? formData.role : 'viewer';
const payload = { firstName: formData.firstName, lastName: formData.lastName, email: formData.email, isActive: !!formData.isActive, role: safeRole };
        const r = await userService.update(formData.id, payload);
        if (!r.success) throw new Error(r.message || 'Failed to update');
        // Sync dashboard permission to role after update
        try {
          await permissionService.save({ subjectType: 'user', subjectId: formData.id, permission: safeRole, resource: 'wqms_dashboard' });
        } catch (_) { /* non-blocking */ }
        setToasts((t) => [...t, { id: Date.now(), type: 'success', message: 'User updated' }]);
      }
      await loadUsers();
      setShowModal(false);
      startCreate();
    } catch (e) {
      setToasts((t) => [...t, { id: Date.now(), type: 'error', message: e.message || 'Action failed' }]);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
  const target = users.find((u) => String(u.id) === String(id));

  if (isLimitedManager && (String(target?.role || '').toLowerCase() !== 'viewer' || !isOwnedByMe(target))) {
    setToasts((t) => [...t, { id: Date.now(), type: 'error', message: 'You can delete only users you created' }]);
    return;
  }

  if (!window.confirm('Delete this user?')) return;
    try {
      const r = await userService.remove(id);
      if (!r.success) throw new Error(r.message || 'Failed to delete');
      await loadUsers();
      setToasts((t) => [...t, { id: Date.now(), type: 'success', message: 'User deleted' }]);
    } catch (e) {
      setToasts((t) => [...t, { id: Date.now(), type: 'error', message: e.message || 'Delete failed' }]);
    }
  };

  // KPIs
  const kpis = useMemo(() => {
    const total = users.length;
    const active = users.filter((u) => u.isActive).length;
    const inactive = total - active;
    return { total, active, inactive };
  }, [users]);

  const fetchStations = useCallback(async () => {
    setStationsLoading(true);
    setStationsError('');
    try {
      const r = await wqmsService.getStations('all', undefined, undefined, undefined, true);
      if (r.success) setStations(r.data.stations || []);
      else setStationsError('Unable to load stations');
    } catch (e) {
      setStationsError('Network error while loading stations');
    } finally {
      setStationsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canAccessUsers) fetchStations();
  }, [fetchStations, canAccessUsers]);

  const stationKpis = useMemo(() => {
    const count = stations.length;
    const avg = (arr) => (arr.length ? (arr.reduce((a, b) => a + Number(b || 0), 0) / arr.length) : null);
    const ph = avg(stations.map((s) => s.ph_avg).filter((v) => Number.isFinite(Number(v))));
    const temp = avg(stations.map((s) => s.temperature_c_avg).filter((v) => Number.isFinite(Number(v))));
    const turb = avg(stations.map((s) => s.turbidity_ntu_avg).filter((v) => Number.isFinite(Number(v))));
    return { count, ph, temp, turb };
  }, [stations]);

  // Filter + search + sort
  const processedUsers = useMemo(() => {
    let list = filterVisibleUsers(users);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((u) => `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
    }
    if (activeFilter === 'active') list = list.filter((u) => u.isActive);
    if (activeFilter === 'inactive') list = list.filter((u) => !u.isActive);
    list.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      const va = a[sortKey];
      const vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return -1 * dir;
      if (vb == null) return 1 * dir;
      if (typeof va === 'string') return va.localeCompare(vb) * dir;
      if (typeof va === 'boolean') return (va === vb ? 0 : va ? 1 : -1) * dir;
      // dates or numbers
      return ((new Date(va)).getTime() - (new Date(vb)).getTime()) * dir;
    });
    return list;
  }, [users, query, activeFilter, sortKey, sortDir, filterVisibleUsers]);

  // No pagination – show all results with scrollable table

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const exportCsv = () => {
    const rows = [['First Name', 'Last Name', 'Email', 'Active', 'Created At']];
    processedUsers.forEach((u) => {
      rows.push([u.firstName, u.lastName, u.email, u.isActive ? 'Yes' : 'No', new Date(u.createdAt).toISOString()]);
    });
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'users.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!canAccessUsers) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="App admin-theme">
      <div className="wqms-dashboard" style={{ paddingTop: 0, background: 'transparent' }}>
        <div className="admin-users-header">
          <div className="admin-content-shell" style={{ paddingBottom: 0 }}>
            <div className="dashboard-header">
            <div className="header-left">
              <h1 className="dashboard-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <UsersIcon size={24} /> Users
              </h1>
              <p className="dashboard-subtitle">Administer access and see key water insights</p>
            </div>
            <div className="header-right">
              <Link className="admin-btn admin-btn-secondary" to="/dashboard">
                <ArrowLeft size={18} /> Back
              </Link>
              {canCreateUsers && (
              <button type="button" className="admin-btn admin-btn-primary" onClick={startCreate}>
                <Plus size={18} /> New User
              </button>
              )}
              {EXPORT_BUTTONS_VISIBLE && (
              <button type="button" className="admin-btn admin-btn-secondary" onClick={exportCsv}>
                <Download size={18} /> Export CSV
              </button>
              )}
              {canViewActivityLogs && (
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={() => setShowActivityLogs(true)}
                >
                  Activity Logs
                </button>
              )}
            </div>
            </div>
          </div>
        </div>

        <div className="admin-content-shell">
          {error && (
            <div className="admin-alert-error">{error}</div>
          )}

        <div className="admin-kpi-grid">
          <div className="admin-kpi-card">
            <h3>{kpis.total}</h3>
            <p>Total Users</p>
          </div>
          <div className="admin-kpi-card">
            <h3>{kpis.active}</h3>
            <p>Active</p>
          </div>
          <div className="admin-kpi-card">
            <h3>{kpis.inactive}</h3>
            <p>Inactive</p>
          </div>
        </div>

        <div className="admin-filter-bar">
          <div className="admin-filter-group">
            <Search size={18} color="#64748b" />
            <input
              placeholder="Search by name or email"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="time-select"
              style={{ padding: 10 }}
            />
          </div>
          <div className="admin-filter-group" style={{ flex: '0 1 auto' }}>
            <Filter size={18} color="#64748b" />
            <select className="time-select" value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)} style={{ padding: 10, minWidth: 140 }}>
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="admin-table-wrap">
          {loading ? (
            <div style={{ padding: 20, color: '#64748b', fontWeight: 600 }}>Loading users...</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('firstName')}>
                    Name {sortKey === 'firstName' ? (sortDir === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />) : null}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('email')}>
                    Email {sortKey === 'email' ? (sortDir === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />) : null}
                  </th>
                  <th>Role</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('isActive')}>
                    Active {sortKey === 'isActive' ? (sortDir === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />) : null}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('createdAt')}>
                    Created {sortKey === 'createdAt' ? (sortDir === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />) : null}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {processedUsers.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#eef2ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
                          {(u.firstName?.[0] || 'U').toUpperCase()} {(u.lastName?.[0] || '').toUpperCase()}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                          <span style={{ fontWeight: 600 }}>{u.firstName} {u.lastName}</span>
                          <span style={{ color: '#64748b', fontSize: 12 }}>{u.email}</span>
                        </div>
                      </div>
                    </td>
                    <td>{u.email}</td>
                    <td style={{ textTransform: 'capitalize' }}>
<span className="chip">{getRoleLabel(u.role || 'viewer')}</span>
                    </td>
                    <td>
                      {u.isActive ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#2563eb' }}><CheckCircle2 size={16} /> Active</span>
                      ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#6b7280' }}><CircleSlash2 size={16} /> Inactive</span>
                      )}
                    </td>
                    <td>{new Date(u.createdAt).toLocaleString()}</td>
                    <td>
                      <div className="admin-table-actions">
                        <button type="button" className="admin-btn admin-btn-sm admin-btn-secondary" onClick={() => startEdit(u)}>Edit</button>
                        <button type="button" className="admin-btn admin-btn-sm admin-btn-danger" onClick={() => remove(u.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!loading && processedUsers.length === 0 && (
          <div className="admin-empty-state">
            <h4>No users found</h4>
            <p>Try adjusting filters or create a new user to get started.</p>
            {canCreateUsers && (
            <button type="button" className="admin-btn admin-btn-primary" onClick={startCreate}>
              <Plus size={16} /> New User
            </button>
            )}
          </div>
        )}

        {/* Pagination removed */}

        {/* Modal for Create/Edit */}
        {showModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1000 }} onClick={() => setShowModal(false)}>
            <div style={{ background: '#fff', width: 'min(640px, 92vw)', margin: '10vh auto', borderRadius: 12, padding: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h2 className="dashboard-title" style={{ fontSize: 20 }}>{formMode === 'create' ? 'Create User' : 'Edit User'}</h2>
                <button className="logout-btn" onClick={() => setShowModal(false)} style={{ background: '#e2e8f0', color: '#1a202c' }}>
                  <X size={18} />
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <input
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="time-select"
                  style={{ padding: 12, borderRadius: 10 }}
                />
                <input
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="time-select"
                  style={{ padding: 12, borderRadius: 10 }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Mail size={16} color="#64748b" />
                  <input
                    placeholder="Email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="time-select"
                    style={{ padding: 12, borderRadius: 10, flex: 1 }}
                  />
                </div>
                {formMode === 'create' && (
                  <input
                    placeholder="Password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="time-select"
                    style={{ padding: 12, borderRadius: 10 }}
                  />
                )}
               {isSuperAdmin && (
<div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
  <div className="field-label">Role</div>
  <div className="segmented" role="group" aria-label="User role">
    {['viewer', 'editor', 'admin'].map(r => (
      <button
        key={r}
        type="button"
        className={`segment ${formData.role === r ? 'active' : ''}`}
        onClick={() => setFormData({ ...formData, role: r })}
      >
        {getRoleLabel(r)}
      </button>
    ))}
  </div>
</div>
               )}
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 4 }}>
                  <input
                    type="checkbox"
                    checked={!!formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  Active
                </label>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'flex-end' }}>
                <button className="logout-btn" onClick={submit} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Save size={18} /> {formMode === 'create' ? (saving ? 'Creating...' : 'Create User') : (saving ? 'Saving...' : 'Save Changes')}
                </button>
                <button className="logout-btn" onClick={() => setShowModal(false)} style={{ background: '#e2e8f0', color: '#1a202c', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <X size={18} /> Close
                </button>
              </div>
            </div>
          </div>
        )}

        </div>

        {canViewActivityLogs && (
          <ActivityLogs
            isOpen={showActivityLogs}
            onClose={() => setShowActivityLogs(false)}
          />
        )}

        {/* Toasts */}
        <div style={{ position: 'fixed', top: 12, right: 12, display: 'flex', flexDirection: 'column', gap: 8, zIndex: 1100 }}>
          {toasts.map((t) => (
            <div key={t.id} style={{ background: t.type === 'success' ? '#ecfdf5' : '#fef2f2', color: t.type === 'success' ? '#065f46' : '#7f1d1d', border: `1px solid ${t.type === 'success' ? '#a7f3d0' : '#fecaca'}`, borderRadius: 10, padding: '8px 12px', boxShadow: '0 6px 20px rgba(0,0,0,0.08)' }}>
              {t.message}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UsersDashboard;

